/**
 * Study-menu icon-chip backgrounds — the soft pastel tiles behind the icon on
 * each Flashcards / Study-Questions / Articles menu row (Bookmark, Quick Set,
 * By Topic, Exam Essentials, All-Questions Testing).
 *
 * These previously lived as raw `icon_bg` hex literals duplicated across every
 * brand's app_constant menus in both apps. They are hoisted here so colors come
 * only from @learnzapp/core/theme (no hex literals in the apps).
 *
 * Brand-agnostic and mode-independent BY DESIGN: the same tints render in light
 * and dark, exactly matching the prior values (this is a value-preserving move,
 * not a restyle). Pinned in __tests__/theme.test.js. Exposed on every theme as
 * `theme.menu_chips` so a brand could one day override; resolve via
 * getMenuChipBackground so web + mobile map a menu item's `key` identically.
 */
export const MENU_CHIP_COLORS = {
  bookmark: '#FFEFED',   // Bookmark
  quick: '#E6FBFF',      // Quick Set
  topic: '#F3FFD9',      // By Topic(s)
  essentials: '#FFF5E6', // Exam Essentials / All Cards
  testing: '#E6E6FA',    // All Questions / Articles (Testing)
};

// Menu-item `key` → chip token. Several keys intentionally share a chip
// (by_topic + by_topic_free_model; exam + all_cards; the two testing sets).
//
// This map is the single source of truth for which menu rows get a pastel chip.
// When a NEW chip-bearing key is added to a GET_*_MENU config, add it here too —
// an unmapped key falls back silently (transparent on mobile, primary color on
// web). __tests__/menuChipCoverage.test.js scans the shipped configs and fails
// CI if a menu key is neither mapped here nor an allowlisted fallback.
const KEY_TO_CHIP = {
  bookmark: 'bookmark',
  quick: 'quick',
  by_topic: 'topic',
  by_topic_free_model: 'topic',
  exam: 'essentials',
  all_cards: 'essentials',
  all_questions_testing: 'testing',
  all_articles_testing: 'testing',
};

/**
 * Resolve the chip background for a menu item's `key`.
 * @param {string} key       the menu item's `key` (e.g. 'bookmark', 'exam')
 * @param {object} [theme]   a theme object; `theme.menu_chips` wins if present
 * @param {string} [fallback] returned when the key is unknown
 * @returns {string|undefined}
 */
export function getMenuChipBackground(key, theme, fallback) {
  const token = KEY_TO_CHIP[key];
  const chips = (theme && theme.menu_chips) || MENU_CHIP_COLORS;
  return (token && chips[token]) || fallback;
}
